import React, { FC, useEffect } from 'react';
import { Modal, Row, Col, Form } from 'antd';
import { Button } from '@cloudcar-app/cloudcar-components';
import ConfirmModal from 'components/modals/ConfirmModal';
import { useMutation } from '@apollo/client';
import operations from 'apollo/operations';
import {
  CreateProposalInput,
  Customer,
  Insurance,
  InsuranceSimulationProduct,
  PaymentType,
} from 'apollo/types';
import communes from 'utils/comunes';
import { trimFormValues } from 'utils/trimFormValues';
import styles from './InsuranceProposalFormModal.module.scss';
import HdiModal from './HdiModal';
import ZurichModal from './ZurichModal';
import BciModal from './BciProposal';

interface InsuranceProposalFormModalProps {
  visible: boolean;
  handleOk: () => void;
  handleCancel: () => void;
  selectedProduct: InsuranceSimulationProduct;
  insuranceData?: Insurance;
  customerData?: Customer;
}

const InsuranceProposalFormModal: FC<InsuranceProposalFormModalProps> = (
  props: InsuranceProposalFormModalProps,
) => {
  const {
    visible,
    handleOk,
    handleCancel,
    selectedProduct,
    insuranceData,
    customerData,
  } = props;
  const [form] = Form.useForm();

  const [sendProposal, setSendProposal] = React.useState<boolean>(false);

  const [createProposal, { data: createProposalData, loading }] = useMutation<{
    createProposalInput: CreateProposalInput;
    createProposal: string;
  }>(operations.CREATE_PROPOSAL);

  const getCommune = (communeCode: string) => {
    if (selectedProduct.insuranceProvider === 'BCI') {
      return communes.bciCommunes.find((c) => c.code.toString() === communeCode)
        ?.name;
    }
    if (selectedProduct.insuranceProvider === 'HDI') {
      return communes.hdiCommunes.find((c) => c.code.toString() === communeCode)
        ?.name;
    }

    return communes.zurichCommunes.find(
      (c) => c.code.toString() === communeCode,
    )?.name;
  };

  const createProposalHandler = async () => {
    let input = {
      proposalProductId: selectedProduct?.productId,
      carColor: form.getFieldValue('carColor') as string,
      carEngineNumber: form.getFieldValue('carEngineId') as string,
      carChassis: form.getFieldValue('carChassis') as string,
      carPlate: form.getFieldValue('carPlate') as string,
      invoiceDate: form.getFieldValue('invoiceDate') as string,
      dealer: form.getFieldValue('carDealer') as string,
      invoiceId: form.getFieldValue('invoiceId') as string,
      issuerRut: form.getFieldValue('issuerRut') as string,
      proposalInsured: {
        city: form.getFieldValue('insuredCity') as string,
        gender: form.getFieldValue('insuredGender') as string,
        addressType: form.getFieldValue('insuredAddressType') as string,
        address: form.getFieldValue('insuredAddress') as string,
        addressNumber: form.getFieldValue('insuredAddressNumber') as string,
        commune: getCommune(form.getFieldValue('insuredCommune') as string),
        communeCode: form.getFieldValue('insuredCommune') as string,
        birthday: form.getFieldValue('insuredBirthday') as string,
      },
    } as CreateProposalInput;

    if (
      insuranceData?.insuranceInput?.rut !== customerData?.rut ||
      insuranceData?.insuranceInput.businessName
    ) {
      input.proposalCustomer = {
        rut: form.getFieldValue('rut') as string,
        addressType: form.getFieldValue('addressType') as string,
        name: form.getFieldValue('name') as string,
        gender: form.getFieldValue('gender') as string,
        firstSurname: form.getFieldValue('firstSurname') as string,
        email: form.getFieldValue('email') as string,
        phone: form.getFieldValue('phone') as string,
        address: form.getFieldValue('address') as string,
        addressNumber: form.getFieldValue('addressNumber') as string,
        commune: getCommune(form.getFieldValue('commune') as string),
        communeCode: form.getFieldValue('commune') as string,
        birthday: form.getFieldValue('birthday') as string,
      };
    }

    if (
      !!insuranceData?.insuranceInput.paymentType &&
      insuranceData?.insuranceInput.paymentType !== PaymentType.Cash &&
      selectedProduct.insuranceProvider !== 'BCI'
    ) {
      input = {
        ...input,
        cardInformation: undefined,
        paymentDay: form.getFieldValue('paymentDay') as string,
      };
    }
    await createProposal({
      variables: {
        createProposalInput: trimFormValues(input),
      },
      refetchQueries: [
        {
          query: operations.GET_INSURANCE,
        },
      ],
    });
  };

  useEffect(() => {
    if (createProposalData?.createProposal) {
      handleOk();
      setSendProposal(false);
    }
  }, [createProposalData, loading]);

  const getProposalModal = () => {
    if (selectedProduct?.insuranceProvider === 'HDI') {
      return (
        <HdiModal
          form={form}
          insuranceData={insuranceData}
          customerData={customerData}
        />
      );
    }

    if (selectedProduct?.insuranceProvider === 'CHILEAN') {
      return (
        <ZurichModal
          form={form}
          insuranceData={insuranceData}
          customerData={customerData}
        />
      );
    }

    if (selectedProduct?.insuranceProvider === 'BCI') {
      return (
        <BciModal
          form={form}
          insuranceData={insuranceData}
          customerData={customerData}
        />
      );
    }

    return null;
  };

  return (
    <>
      {sendProposal && (
        <ConfirmModal
          visible={sendProposal}
          loading={loading}
          handleOk={() => {
            createProposalHandler();
          }}
          handleCancel={() => {
            setSendProposal(false);
          }}
        />
      )}
      <Modal
        visible={visible}
        onCancel={handleCancel}
        footer={null}
        width="60%"
        bodyStyle={{
          padding: '36px 36px 24px 36px',
          overflowY: 'auto',
          maxHeight: 'calc(100vh - 200px)',
        }}
        style={{ top: '10%', bottom: '50%', overflowY: 'scroll' }}
      >
        <Row>
          <Col xs={24}>
            <h1 className={styles.ModalTitle}>Propuesta seguro</h1>
          </Col>
        </Row>
        <Form
          form={form}
          onFinish={() => {
            setSendProposal(true);
          }}
        >
          {getProposalModal()}
          <Row className={styles.ButtonRow} justify="end" gutter={8}>
            <Col>
              <Button onClick={handleCancel} mode="secondary">
                Cancelar
              </Button>
            </Col>
            <Col>
              <Button htmlType="submit">Generar</Button>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
};

InsuranceProposalFormModal.defaultProps = {
  insuranceData: undefined,
  customerData: undefined,
};

export default InsuranceProposalFormModal;
